import { Modal, Spin } from "antd";
import React, { useEffect, useState } from "react";
import * as pdfjsLib from "pdfjs-dist";
import * as mammoth from "mammoth";

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

export interface JobDescriptionModalProps {
  jdFile: File | null;
  open: boolean;
  onClose: () => void;
}

const JobDescriptionModal: React.FC<JobDescriptionModalProps> = ({
  jdFile,
  open,
  onClose,
}) => {
  const [content, setContent] = useState<string>("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!jdFile) {
      setContent("");
      return;
    }

    const readFileContent = async () => {
      setLoading(true);
      try {
        let extractedText = "";

        if (jdFile.type === "application/pdf") {
          const pdfData = await jdFile.arrayBuffer();
          const pdf = await pdfjsLib.getDocument({ data: pdfData }).promise;
          let text = "";

          for (let i = 1; i <= pdf.numPages; i++) {
            const page = await pdf.getPage(i);
            const content = await page.getTextContent();
            text +=
              content.items
                .map((it: any) => it.str)
                .join(" ")
                .replace(/•\s*/g, "\n• ")
                .replace(/▪\s*/g, "\n▪ ")
                .replace(/●\s*/g, "\n● ")
                .replace(/\n{2,}/g, "\n\n") + "\n\n";
          }
          extractedText = text.trim();
        } else if (
          jdFile.type ===
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        ) {
          const arrayBuffer = await jdFile.arrayBuffer();
          const result = await mammoth.convertToHtml(
            { arrayBuffer },
            {
              styleMap: ["p[style-name='List Paragraph'] => ul > li:fresh"],
            }
          );
          extractedText = result.value;
        } else if (jdFile.type.startsWith("text/")) {
          extractedText = await jdFile.text();
        } else {
          extractedText = "Preview not supported for this file type.";
        }

        const formatted = extractedText
          .replace(/•\s*/g, "\n• ")
          .replace(/▪\s*/g, "\n▪ ")
          .replace(/●\s*/g, "\n● ");

        setContent(formatted);
      } catch (err) {
        console.error("Error reading file:", err);
        setContent("Failed to load file content.");
      } finally {
        setLoading(false);
      }
    };

    readFileContent();
  }, [jdFile]);

  return (
    <Modal
      title="Job Description"
      open={open}
      onCancel={onClose}
      footer={null}
      width={800}
    >
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <Spin tip="Loading job description..." />
        </div>
      ) : (
        <div className="max-h-[70vh] overflow-y-auto text-gray-800 leading-relaxed whitespace-pre-wrap">
          {content ? (
            jdFile?.type.includes("wordprocessingml") ? (
              <div
                className="prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: content }}
              />
            ) : (
              <pre className="whitespace-pre-wrap text-sm text-gray-700">
                {content}
              </pre>
            )
          ) : (
            <p className="text-gray-500 italic">
              No job description available.
            </p>
          )}
        </div>
      )}
    </Modal>
  );
};

export default JobDescriptionModal;